import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, MapPin } from 'lucide-react';
import { ListingGrid } from '../components/ListingGrid';
import { Filters } from '../components/marketplace/Filters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useApp } from '../context/AppContext';
import { useApi } from '../hooks/useApi';
import { DEFAULT_FILTERS, filtersToParams } from '../lib/listings';

const SORT_OPTIONS = ['recent', 'popular', 'priceAsc', 'priceDesc'];

const useDebounced = (value, ms = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
};

const Toolbar = ({ filters, setFilters, onReset }) => {
  const { t } = useApp();
  return (
    <div className="flex items-center gap-2">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" className="lg:hidden" data-testid="mobile-filters-button"><SlidersHorizontal className="w-4 h-4 mr-2" /> {t('mkt.filters')}</Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80 overflow-y-auto">
          <div className="pt-6"><Filters filters={filters} setFilters={setFilters} onReset={onReset} /></div>
        </SheetContent>
      </Sheet>
      <Input placeholder={t('hero.search')} value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className="w-56 bg-white" data-testid="marketplace-search-input" />
      <Select value={filters.sort} onValueChange={(sort) => setFilters((f) => ({ ...f, sort }))}>
        <SelectTrigger className="w-44 bg-white" data-testid="marketplace-sort-select"><SelectValue /></SelectTrigger>
        <SelectContent>{SORT_OPTIONS.map((s) => <SelectItem key={s} value={s}>{t(`mkt.${s}`)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

const EmptyState = ({ onReset }) => {
  const { t } = useApp();
  return (
    <div className="text-center py-20 bg-white rounded-2xl border border-[#EFEAE0]" data-testid="marketplace-empty-state">
      <MapPin className="w-12 h-12 mx-auto text-[#A8A294] mb-3" />
      <p className="text-[#3F3A30] font-medium">{t('mkt.noResults')}</p>
      <Button onClick={onReset} variant="outline" className="mt-4">{t('mkt.reset')}</Button>
    </div>
  );
};

const Marketplace = () => {
  const { t } = useApp();
  const [params] = useSearchParams();
  const urlQ = params.get('q') || '';
  const urlCat = params.get('cat') || 'all';
  const urlCountry = params.get('country') || 'all';
  const urlDiaspora = params.get('diaspora') === '1';

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, q: urlQ, cat: urlCat, country: urlCountry, diaspora: urlDiaspora });
  useEffect(() => {
    setFilters((f) => ({ ...f, q: urlQ, cat: urlCat, country: urlCountry, diaspora: urlDiaspora }));
  }, [urlQ, urlCat, urlCountry, urlDiaspora]);

  const debounced = useDebounced(filters);
  const apiParams = useMemo(() => filtersToParams(debounced), [debounced]);
  const { data: results, loading, error, reload } = useApi('/listings', apiParams);
  const reset = () => setFilters(DEFAULT_FILTERS);
  const count = results?.length ?? 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-[#1A1A1A]">{t('mkt.title')}</h1>
          <p className="text-[#7A7468] mt-1" data-testid="marketplace-results-count">{loading ? t('common.loading') : `${count} ${t('mkt.results')}`}</p>
        </div>
        <Toolbar filters={filters} setFilters={setFilters} onReset={reset} />
      </div>
      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden lg:block sticky top-24 self-start bg-white p-6 rounded-2xl border border-[#EFEAE0]">
          <Filters filters={filters} setFilters={setFilters} onReset={reset} />
        </aside>
        <div>
          {!loading && !error && count === 0 ? <EmptyState onReset={reset} /> : (
            <ListingGrid listings={results} loading={loading} error={error} onRetry={reload} compact cols="grid-cols-2 md:grid-cols-3" testId="marketplace-results-grid" />
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
